import { useState } from "react";
import {
  Box, Drawer, List, ListItemButton, ListItemIcon, ListItemText,
  Toolbar, AppBar, Typography, IconButton, Button, useMediaQuery, useTheme as useMuiTheme,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Article as ArticleIcon,
  ArticleOutlined as PostIcon,
  Category as CategoryIcon,
  Settings as SettingsIcon,
  Language as WebIcon,
  KeyboardArrowLeft as KeyboardArrowLeftIcon,
} from "@mui/icons-material";
import { Outlet, NavLink, useNavigate } from "react-router-dom";

const DRAWER_WIDTH = 220;

const navItems = [
  { label: "Dashboard", to: "/cms", icon: <DashboardIcon fontSize="small" />, end: true },
  { label: "Pages", to: "/cms/pages", icon: <ArticleIcon fontSize="small" /> },
  { label: "Posts", to: "/cms/posts", icon: <PostIcon fontSize="small" /> },
  { label: "Categories", to: "/cms/categories", icon: <CategoryIcon fontSize="small" /> },
  { label: "Settings", to: "/cms/settings", icon: <SettingsIcon fontSize="small" /> },
];

export default function CmsLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("md"));
  const navigate = useNavigate();

  const drawerContent = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Toolbar sx={{ px: 2 }}>
        <WebIcon sx={{ mr: 1, color: "primary.safeOnLight" }} />
        <Typography variant="subtitle1" fontWeight={700} noWrap>
          Website CMS
        </Typography>
      </Toolbar>
      <List dense sx={{ flex: 1, px: 1 }}>
        {navItems.map(({ label, to, icon, end }) => (
          <ListItemButton
            key={to}
            component={NavLink}
            to={to}
            end={end}
            sx={{
              borderRadius: 1,
              mb: 0.5,
              "&.active": { bgcolor: "primary.light", color: "primary.contrastText",
                "& .MuiListItemIcon-root": { color: "primary.contrastText" } },
            }}
          >
            <ListItemIcon sx={{ minWidth: 32 }}>{icon}</ListItemIcon>
            <ListItemText primary={label} />
          </ListItemButton>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      {/* Top AppBar */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          ml: { md: `${DRAWER_WIDTH}px` },
          boxShadow: "none",
          backgroundColor: "#f1f1f1",
          borderBottom: "1px solid #e7e7e7",
          zIndex: (t) => t.zIndex.drawer + 1,
        }}
      >
        <Toolbar sx={{ display: "flex", justifyContent: "space-between" }}>
          <IconButton
            color="inherit"
            edge="start"
            onClick={() => setMobileOpen(true)}
            sx={{ mr: 2, display: { md: "none" }, color: "primary.safeOnLight" }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1 }} />
          <Button
            endIcon={<KeyboardArrowLeftIcon />}
            onClick={() => navigate("/")}
            sx={{
              textTransform: "capitalize",
              color: "primary.safeOnLight",
              fontSize: "0.9rem",
              fontWeight: 600,
              "&:hover": {
                backgroundColor: "rgba(0,0,0,0.05)",
              },
            }}
          >
            Exit CMS
          </Button>
        </Toolbar>
      </AppBar>

      {/* Sidebar */}
      <Box component="nav" sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}>
        <Drawer
          variant={isMobile ? "temporary" : "permanent"}
          open={isMobile ? mobileOpen : true}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            "& .MuiDrawer-paper": {
              width: DRAWER_WIDTH,
              boxSizing: "border-box",
              borderRight: "1px solid",
              borderColor: "divider",
            },
          }}
        >
          {drawerContent}
        </Drawer>
      </Box>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flex: 1,
          p: 3,
          mt: 8,
          bgcolor: "grey.50",
          minHeight: "100vh",
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
}
